import { useCallback, useEffect, useRef } from 'react'

import { useEventSubscription } from 'tripPlanner/assistant/contexts/TripPlannerEventBus'
import {
  trackFirstMessageSent,
  trackSessionEnd,
  trackSessionStart,
} from 'tripPlanner/utils/chatAnalytics'

interface UseChatSessionTrackingArgs {
  chatId: number | undefined
  existingRouteChatId: number | undefined
  /** A chat a WhatsApp handoff just created opens like an existing one but starts a new session. */
  handoffChatId: number | undefined
}

export function useChatSessionTracking({
  chatId,
  existingRouteChatId,
  handoffChatId,
}: UseChatSessionTrackingArgs) {
  const sessionStartTimestamp = useRef<number>(Date.now())
  const firstMessageFired = useRef(false)
  const lastTrackedChatId = useRef<number | undefined>(undefined)
  const createdChatId = useRef<number | undefined>(undefined)
  const countedHandoffChatId = useRef<number | undefined>(undefined)
  const pendingFirstMessageMs = useRef<number | undefined>(undefined)

  useEventSubscription('chat_created', (event) => {
    createdChatId.current = event.chatId
  })

  // A session is a chat. The landing starts the clock for the chat its first send creates.
  useEffect(() => {
    if (chatId === lastTrackedChatId.current) return
    lastTrackedChatId.current = chatId
    const pendingMs = pendingFirstMessageMs.current
    const isCreatedBySend = chatId !== undefined && chatId === createdChatId.current
    pendingFirstMessageMs.current = undefined
    createdChatId.current = undefined

    if (isCreatedBySend) {
      trackSessionStart(chatId, 'new')
      if (pendingMs !== undefined) {
        trackFirstMessageSent(chatId, pendingMs)
      }
      return
    }

    firstMessageFired.current = false
    sessionStartTimestamp.current = Date.now()
    if (chatId !== undefined) {
      const isNewHandoff = chatId === handoffChatId && countedHandoffChatId.current !== chatId
      if (isNewHandoff) countedHandoffChatId.current = chatId
      const isReturning = existingRouteChatId !== undefined && !isNewHandoff
      trackSessionStart(chatId, isReturning ? 'returning' : 'new')
    }
  }, [chatId, existingRouteChatId, handoffChatId])

  useEffect(() => {
    const handlePageHide = () => {
      const durationMs = Date.now() - sessionStartTimestamp.current
      trackSessionEnd(lastTrackedChatId.current, durationMs)
    }

    window.addEventListener('pagehide', handlePageHide)
    return () => window.removeEventListener('pagehide', handlePageHide)
  }, [])

  return useCallback(() => {
    if (firstMessageFired.current) {
      return
    }
    firstMessageFired.current = true
    const timeToFirstMessageMs = Date.now() - sessionStartTimestamp.current
    if (chatId !== undefined) {
      trackFirstMessageSent(chatId, timeToFirstMessageMs)
    } else {
      pendingFirstMessageMs.current = timeToFirstMessageMs
    }
  }, [chatId])
}
